// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Column definitions for the Maryland bills browser table.
 */
import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DarkTooltipContent } from '@/components/ui/dark-tooltip'
import { AlertCircle, CheckCircle2, Eye, FileText, Loader2, MoreVertical, XCircle } from 'lucide-react'
import type { MarylandBillMetadata } from '../services/maryland-bills.service'

export interface AgentRunOption {
  agentId: string
  label: string
  pipelineId: string
}

export interface UseMarylandBillsColumnsParams {
  session: string | null
  onViewDetails: (bill: MarylandBillMetadata) => void
  onViewAmendments: (bill: MarylandBillMetadata) => void
  agents?: AgentRunOption[]
  onRunAgent?: (bill: MarylandBillMetadata, pipelineId: string) => void
  reviewStatuses?: Map<string, string>
}

export function useMarylandBillsColumns({
  session,
  onViewDetails,
  onViewAmendments,
  agents,
  onRunAgent,
  reviewStatuses,
}: UseMarylandBillsColumnsParams): ColumnDef<MarylandBillMetadata>[] {
  return useMemo(
    () => [
      {
        accessorKey: 'BillNumber',
        header: 'Bill',
        cell: ({ row }) => {
          const bill = row.original
          const billTestId = `lr-maryland-${bill.BillNumber.toLowerCase()}`
          const url = `https://mgaleg.maryland.gov/mgawebsite/Legislation/Details/${bill.BillNumber}`
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary hover:underline"
              data-testid={`${billTestId}-external`}
            >
              {bill.BillNumber}
            </a>
          )
        },
        size: 100,
      },
      {
        id: 'pdf',
        header: () => <span className="text-center w-full block">PDF</span>,
        cell: ({ row }) => {
          const { BillNumber, BillVersion } = row.original
          const billTestId = `lr-maryland-${BillNumber.toLowerCase()}`
          if (!BillVersion || !session) return null
          const billType = BillNumber.slice(0, 2).toLowerCase()
          const url = `https://mgaleg.maryland.gov/${session}/bills/${billType}/${BillNumber.toLowerCase()}${BillVersion.toLowerCase()}.pdf`
          return (
            <div className="flex justify-center">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                data-testid={`${billTestId}-pdf`}
              >
                <FileText className="h-4 w-4" />
              </a>
            </div>
          )
        },
        size: 50,
      },
      {
        accessorKey: 'Title',
        header: 'Title',
        meta: { className: 'w-full max-w-0' },
        cell: ({ row }) => {
          const { Title, Synopsis } = row.original
          return (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help truncate">
                    <div className="font-medium line-clamp-1">{Title}</div>
                    {Synopsis && (
                      <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {Synopsis}
                      </div>
                    )}
                  </div>
                </TooltipTrigger>
                <DarkTooltipContent side="bottom" align="start" className="max-w-xl p-3 whitespace-normal break-words">
                  <div className="space-y-2">
                    <div className="font-medium text-sm whitespace-normal">{Title}</div>
                    {Synopsis && (
                      <div className="text-xs opacity-80 whitespace-normal">{Synopsis}</div>
                    )}
                  </div>
                </DarkTooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        },
      },
      {
        id: 'amendments',
        header: () => <span className="text-center w-full block">Amendments</span>,
        cell: ({ row }) => {
          if (!row.original.has_amendments) return null
          return (
            <div className="flex justify-center">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewAmendments(row.original)}
                      data-testid={`lr-maryland-${row.original.BillNumber.toLowerCase()}-amendments`}
                    >
                      <AlertCircle className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <DarkTooltipContent>
                    <span className="text-xs">View amendments</span>
                  </DarkTooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )
        },
        size: 90,
      },
      {
        accessorKey: 'CrossfileBillNumber',
        header: 'Crossfile',
        size: 100,
      },
      // Score and analysis columns are injected by useRendererColumns
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const bill = row.original
          const billTestId = `lr-maryland-${bill.BillNumber.toLowerCase()}`
          const reviewStatus = reviewStatuses?.get(bill.BillNumber)
          const isRunning = reviewStatus === 'running'
          return (
            <div className="flex items-center gap-1">
              <span className="sr-only" data-testid={`${billTestId}-status`}>
                {reviewStatus ?? 'idle'}
              </span>
              {agents && agents.length > 0 && onRunAgent && (
                <>
                  {reviewStatus === 'completed' && (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid={`${billTestId}-status-completed`} />
                  )}
                  {reviewStatus === 'error' && (
                    <XCircle className="h-4 w-4 text-destructive" data-testid={`${billTestId}-status-error`} />
                  )}
                  {isRunning ? (
                    <Loader2 className="h-4 w-4 animate-spin" data-testid={`${billTestId}-status-running`} />
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`${billTestId}-actions`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {agents.map((a) => (
                          <DropdownMenuItem
                            key={a.agentId}
                            onClick={() => onRunAgent(bill, a.pipelineId)}
                            data-testid={`${billTestId}-run-${a.agentId}`}
                          >
                            Run &ldquo;{a.label}&rdquo;
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewDetails(bill)}
                data-testid={`${billTestId}-details`}
              >
                <Eye className="h-4 w-4 mr-1" />
                Details
              </Button>
            </div>
          )
        },
      },
    ],
    [session, onViewDetails, onViewAmendments, agents, onRunAgent, reviewStatuses],
  )
}
